import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

// material-ui
import { styled } from '@mui/material/styles';
import {
    Box,
    Collapse,
    ClickAwayListener,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Typography,
} from '@mui/material';

// project import
import NavItem from './NavItem';

// assets
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// types
import { NavItemType } from 'types/menu';
import { MenuContext } from 'contexts';
import { securelyRetrieveData } from 'utils';

type VirtualElement = {
    getBoundingClientRect: () => ClientRect | DOMRect;
    contextElement?: Element;
};

// mini-menu - wrapper
const PopperStyled = styled(Popper)(({ theme }) => ({
    overflow: 'visible',
    zIndex: 1202,
    minWidth: 180,
    '&:before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        top: 38,
        left: -5,
        width: 10,
        height: 10,
        backgroundColor: theme.palette.background.paper,
        transform: 'translateY(-50%) rotate(45deg)',
        zIndex: 120,
        borderLeft: `1px solid ${theme.palette.grey.A800}`,
        borderBottom: `1px solid ${theme.palette.grey.A800}`
    }
}));

// ==============================|| NAVIGATION - LIST COLLAPSE ||============================== //

interface Props {
    menu: NavItemType;
    level: number;
    parentId: string;
    setSelectedItems: React.Dispatch<React.SetStateAction<string | undefined>>;
    selectedItems: string | undefined;
    setSelectedLevel: React.Dispatch<React.SetStateAction<number>>;
    selectedLevel: number;
}

const NavCollapse = ({ menu, level, parentId, setSelectedItems, selectedItems, setSelectedLevel, selectedLevel }: Props) => {

    const { setMenuState } = useContext(MenuContext);

    const retrievedDrawerOpen = securelyRetrieveData("drawerOpen");
    const drawerOpen  = retrievedDrawerOpen !== undefined ? retrievedDrawerOpen : true;
    const Navigation = useNavigate();

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string | null | undefined>(null);
    const [anchorEl, setAnchorEl] = useState<VirtualElement | (() => VirtualElement) | null | undefined>(null);

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLDivElement, MouseEvent> | undefined) => {
        setAnchorEl(null);
        setSelectedLevel(level);
        if (drawerOpen) {
            setOpen(!open);
            setSelected(!selected ? menu.id : null);
            setSelectedItems(!selected ? menu.id : '');
            if (menu.url) Navigation(`${menu.url}`);
        } else {
            setAnchorEl(event?.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const { pathname } = useLocation();
    const openMini = Boolean(anchorEl);

    const checkOpenForParent = (child: NavItemType[], id: string) => {
        child.forEach((item: NavItemType) => {
            if (item.url === pathname) {
                setOpen(true);
                setSelected(id);
            }
        });
    };

    // menu collapse for sub-levels
    useEffect(() => {
        setOpen(false);
        setSelected(null);
        if (openMini) setAnchorEl(null);
        if (menu.children) {
            menu.children.forEach((item: NavItemType) => {
                if (item.children?.length) {
                    checkOpenForParent(item.children, menu.id!);
                }
                if (item.url === pathname) {
                    setSelected(menu.id);
                    setOpen(true);
                }
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, menu.children]);

    useEffect(() => {
        if (menu.url === pathname) {
            setMenuState(menuState => ({ 
                ...menuState,
                openItem: [menu.id!]}));
            setSelected(menu.id);
            setAnchorEl(null);
            setOpen(true);
        }
    }, 
    [pathname, menu]);

    const navCollapse = menu.children?.map((item) => {
        switch (item.type) {
            case 'collapse':
                return (
                    <NavCollapse
                        key={item.id}
                        setSelectedItems={setSelectedItems}
                        setSelectedLevel={setSelectedLevel}
                        selectedLevel={selectedLevel}
                        selectedItems={selectedItems}
                        menu={item}
                        level={level + 1}
                        parentId={parentId}
                    />
                );
            case 'item':
                return <NavItem key={item.id} item={item} level={level + 1} />;
            default:
                return (
                    <Typography key={item.id} variant="h6" color="error" align="center">
                        Fix - Collapse or Item
                    </Typography>
                );
        }
    });
    return (
        <>
            <Box sx={{ paddingX: drawerOpen ? "10px" : "8px", paddingY: "2px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ListItemButton
                    disableRipple
                    selected={selected === menu.id}
                    onClick={handleClick}
                    sx={{
                        height: "46px",
                        display: "flex",
                        ml: (drawerOpen) ? `${(level - 1) * 12}px` : undefined,
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "4px",
                        '&:hover': {
                            bgcolor: "inherit",
                        },
                        '&.Mui-selected': {
                            bgcolor: "inherit",
                            '&:hover': {
                                bgcolor: "inherit"
                            },
                        }
                    }}
                >
                    {(drawerOpen || (!drawerOpen && level === 1)) &&
                        <ListItemIcon
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >  {menu.icon && level === 1 && (
                            <menu.icon //color={selected === menu.id ? "white" : "#BFBABA"}
                                color={"#BFBABA"}
                            />
                        )}
                        </ListItemIcon>
                    }
                    {((drawerOpen) || (level !== 1 && !drawerOpen)) && (
                        <ListItemText
                            primary={
                                <Typography variant="h6" sx={{
                                    marginLeft: "10px",
                                    //color: selected === menu.id ? "white" : "#BFBABA" 
                                    color: (!drawerOpen && level!== 1) ? "black" : "#BFBABA"
                                }}>
                                    {menu.title}
                                </Typography>
                            }
                        />
                    )}
                    {(drawerOpen) &&
                        (openMini || open ? (
                            <KeyboardArrowUpIcon style={{
                                fontSize: '0.625rem', marginLeft: 1,
                                //color: selected === menu.id ? "white" : "#BFBABA"
                                color: "#BFBABA"
                            }} />
                        ) : (
                            <KeyboardArrowDownIcon style={{
                                fontSize: '0.625rem', marginLeft: 1,
                                //color: selected === menu.id ? "white" : "#BFBABA" 
                                color: "#BFBABA"

                            }} />
                        ))}

                    {!drawerOpen && (
                        <PopperStyled
                            open={openMini}
                            anchorEl={anchorEl}
                            style={{
                                zIndex: 2001
                            }}
                            placement='right-start'
                            popperOptions={{
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [-12, 1]
                                        }
                                    }
                                ]
                            }}
                        >
                            <Paper sx={{
                                overflow: 'hidden',
                                mt: 1.5,
                                backgroundImage: 'none',
                            }}>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <Box>{navCollapse}</Box>
                                </ClickAwayListener>
                            </Paper>
                        </PopperStyled>
                    )}
                </ListItemButton>
            </Box>
            {drawerOpen && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List sx={{ p: 0 }}>{navCollapse}</List>
                </Collapse>
            )}
        </>
    )
};

export default NavCollapse;